// internal/WorkspacesPage.jsx — Workspaces (CEO) : instances du moteur + Workspace Factory (langage naturel → proposition → création).
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import InternalLayout, { S, IT } from "@/internal/InternalLayout";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/internal`;
const H = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` });

function Factory({ onCreated, initialIntent }) {
  const [intent, setIntent] = useState(initialIntent || "");
  const [busy, setBusy] = useState(false);
  const [prop, setProp] = useState(null);

  const propose = useCallback(async (text) => {
    const t = (text ?? intent).trim();
    if (!t) return;
    setBusy(true); setProp(null);
    try {
      const r = await fetch(`${API}/factory/propose`, { method: "POST", headers: H(), body: JSON.stringify({ intent: t }) });
      const d = await r.json();
      if (!d.ok) throw new Error(d.message || d.error);
      setProp(d.proposal);
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  }, [intent]);

  useEffect(() => { if (initialIntent) propose(initialIntent); }, [initialIntent]); // eslint-disable-line
  const create = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/factory/create`, { method: "POST", headers: H(), body: JSON.stringify({ proposal: prop, intent }) });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error);
      toast.success(`✅ Workspace « ${prop.name} » créé`);
      setProp(null); setIntent("");
      onCreated();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ ...S.card, padding: 16, marginBottom: 16, border: "1px solid rgba(232,184,75,0.35)" }} data-testid="ws-factory">
      <h2 style={{ ...S.h2, color: IT.gold }}>⚡ Workspace Factory</h2>
      <div style={{ fontSize: 12, color: IT.muted, margin: "4px 0 10px" }}>
        Décrivez un nouveau collaborateur ou métier en langage naturel — AYA génère son espace complet (modules, KPI, permissions, Copilot).
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <input data-testid="factory-intent" value={intent} onChange={(e) => setIntent(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && propose()}
          placeholder='Ex : "J’ai un nouveau collaborateur comptable. Crée son espace."'
          style={{ flex: 1, minWidth: 240, background: "#0D1310", border: `1px solid ${IT.border}`, borderRadius: 10, padding: "10px 13px", color: IT.text, fontSize: 13, outline: "none", fontFamily: "inherit" }} />
        <button data-testid="factory-propose" onClick={() => propose()} disabled={busy || !intent.trim()} style={{ ...S.btn, opacity: busy ? 0.6 : 1 }}>
          {busy && !prop ? "AYA analyse…" : "Proposer →"}
        </button>
      </div>
      {prop && (
        <div style={{ marginTop: 14, padding: 14, borderRadius: 12, background: "rgba(232,184,75,0.06)", border: "1px solid rgba(232,184,75,0.3)" }} data-testid="factory-proposal">
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ width: 36, height: 36, borderRadius: 10, background: `${prop.color}22`, border: `1px solid ${prop.color}66`, color: prop.color, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center" }}>{(prop.owner_name || "?")[0]}</span>
            <span style={{ flex: 1, minWidth: 180 }}>
              <span style={{ display: "block", fontWeight: 900, fontSize: 15 }}>{prop.name}</span>
              <span style={{ fontSize: 11.5, color: IT.muted }}>{prop.owner_name} · {prop.role_title} · Copilot {prop.copilot}</span>
            </span>
          </div>
          <div style={{ fontSize: 12.5, color: IT.text, margin: "10px 0" }}>{prop.summary}</div>
          <div style={{ fontSize: 11.5, color: IT.muted, display: "flex", flexDirection: "column", gap: 4 }}>
            <span>📦 Modules : {(prop.modules || []).map((m) => `${m.icon} ${m.label}`).join(" · ")}</span>
            <span>📊 KPI : {(prop.kpis || []).map((k) => k.label).join(" · ")}</span>
            <span style={{ color: "#F87171" }}>🔒 Actions interdites : {(prop.sensitive_blocked || []).join(", ") || "aucune"}</span>
            <span>🛟 Backup : {prop.backup || "Cédric"}</span>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button data-testid="factory-cancel" onClick={() => setProp(null)} style={{ ...S.ghost, borderColor: "rgba(139,163,148,0.4)", color: IT.muted }}>Annuler</button>
            <button data-testid="factory-create" onClick={create} disabled={busy} style={S.btn}>✓ Créer le workspace</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function WorkspacesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const factoryIntent = location.state?.factoryIntent || null;
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    fetch(`${API}/workspaces`, { headers: H() }).then((r) => r.json()).then((x) => x.ok && setD(x)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <InternalLayout title="Workspaces" subtitle="Chaque collaborateur = une instance du même moteur. Le CEO peut prendre le contrôle de n'importe quel espace.">
      {d?.ceo && <Factory onCreated={load} initialIntent={factoryIntent} />}
      {!d && <div style={{ color: IT.muted, fontSize: 13 }}>Chargement…</div>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 12 }} data-testid="ws-grid">
        {(d?.workspaces || []).filter((w) => !w.is_ceo).map((w) => {
          const g = w.goals?.[0];
          const pct = g ? Math.min(100, Math.round((g.current / g.target) * 100)) : null;
          return (
            <div key={w.slug} style={{ ...S.card, padding: 16, opacity: w.status === "inactive" ? 0.55 : 1 }} data-testid={`ws-card-${w.slug}`}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 38, height: 38, borderRadius: 11, background: `${w.color}22`, border: `1px solid ${w.color}66`, color: w.color, fontWeight: 900, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>{w.owner_name[0]}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontWeight: 900, fontSize: 14 }}>{w.name}</span>
                  <span style={{ fontSize: 11, color: IT.muted }}>{w.owner_name} · {w.role_title}</span>
                </span>
                {w.is_factory && <span style={{ fontSize: 8.5, fontWeight: 900, color: IT.gold, border: "1px solid rgba(232,184,75,0.4)", borderRadius: 999, padding: "2px 7px" }}>FACTORY</span>}
                {w.status === "inactive" && <span style={{ fontSize: 8.5, fontWeight: 900, color: "#F87171", border: "1px solid rgba(248,113,113,0.4)", borderRadius: 999, padding: "2px 7px" }}>INACTIF</span>}
              </div>
              <div style={{ display: "flex", gap: 14, margin: "12px 0 10px", fontSize: 11.5, color: IT.muted }}>
                <span>📋 {w.open_tasks} tâche{w.open_tasks > 1 ? "s" : ""}</span>
                {w.blocked_tasks > 0 && <span style={{ color: "#F87171" }}>🚫 {w.blocked_tasks} bloquée{w.blocked_tasks > 1 ? "s" : ""}</span>}
                <span>🤖 {w.copilot}</span>
              </div>
              {pct != null && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10.5, color: IT.muted, marginBottom: 4 }}>{g.label} — <strong style={{ color: pct >= 100 ? IT.green : IT.text }}>{pct}%</strong></div>
                  <div style={{ height: 5, borderRadius: 99, background: "rgba(255,255,255,0.07)" }}>
                    <div style={{ width: `${pct}%`, height: "100%", borderRadius: 99, background: pct >= 80 ? IT.green : pct >= 50 ? "#F59E0B" : "#F87171" }} />
                  </div>
                </div>
              )}
              <button data-testid={`ws-open-${w.slug}`} onClick={() => navigate(`/internal/ws/${w.slug}`)}
                style={{ ...S.btn, width: "100%", justifyContent: "center", boxSizing: "border-box", background: d?.ceo ? "linear-gradient(180deg,#E8B84B,#C99A2E)" : S.btn.background }}>
                {d?.ceo ? "🔐 Prendre le contrôle" : "Ouvrir mon espace"}
              </button>
            </div>
          );
        })}
      </div>
    </InternalLayout>
  );
}
